import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  Bot, 
  CreditCard, 
  Trophy, 
  BarChart3, 
  Settings, 
  Mail, 
  DollarSign, 
  Ticket, 
  ShieldCheck,
  LogOut,
  Crown
} from 'lucide-react';

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define which roles can access each nav item
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/hosts', label: 'Hosts', icon: <Users size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/templates', label: 'Game Templates', icon: <BookOpen size={20} />, roles: ['super_admin', 'editor'] },
    { path: '/dashboard/ai-generator', label: 'AI Question Generator', icon: <Bot size={20} />, roles: ['super_admin', 'editor'] },
    { path: '/dashboard/subscriptions', label: 'Subscriptions', icon: <CreditCard size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/championships', label: 'Championships', icon: <Trophy size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/analytics', label: 'Analytics', icon: <BarChart3 size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/settings', label: 'Settings', icon: <Settings size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/messages', label: 'Messages', icon: <Mail size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/pricing', label: 'Pricing', icon: <DollarSign size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/coupons', label: 'Coupons', icon: <Ticket size={20} />, roles: ['super_admin'] },
    { path: '/dashboard/admin-users', label: 'Admin Users', icon: <ShieldCheck size={20} />, roles: ['super_admin'] },
  ];

  // Filter nav items based on admin role
  const userRole = admin?.role || 'editor';
  const filteredNavItems = navItems.filter(item => item.roles.includes(userRole));

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-slate-600 to-slate-800 shadow-lg">
        {/* Logo */}
        <div className="p-6 border-b border-slate-500">
          <div className="flex items-center">
            <img 
              src="https://quizzlertrivia.com/img/quizzler-logo-wt.png" 
              alt="Quizzler" 
              style={{ height: '30px', width: '203px' }}
            />
          </div>
          <p className="text-xs text-slate-200 mt-2">Admin Panel</p>
        </div>

        {/* Admin Info */}
        <div className="p-4 border-b border-slate-500 bg-slate-700">
  <p className="text-sm font-semibold text-white">{admin?.name || 'Admin'}</p>
  <p className="text-xs text-slate-200">{admin?.email}</p>
  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-semibold ${
    admin?.role === 'super_admin' ? 'bg-yellow-500 text-yellow-900' : 'bg-slate-500 text-white'
  }`}>
    {admin?.role === 'super_admin' ? <><Crown size={12} className="inline mr-1" /> Super Admin</> : 'Editor'}
  </span>
</div>

        {/* Navigation */}
        <nav className="p-4">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-2 mb-1 rounded-lg transition ${
                  isActive
                    ? 'bg-slate-500 text-white font-semibold'
                    : 'text-slate-100 hover:bg-slate-700'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 w-64 p-4 border-t border-slate-500">
          <button
            onClick={handleLogout}
            className="w-full px-4 py-3 text-left text-slate-100 hover:bg-slate-700 rounded-lg transition flex items-center"
          >
            <span className="mr-3"><LogOut size={20} /></span>
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
